/**
 * ReviewForm Component
 * Form for submitting product reviews
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState, useCallback } from 'react';
import { X, Plus, Upload, ImageIcon, Loader2 } from 'lucide-react';
import { StarRating } from './StarRating';
import { useCreateReview } from '@/features/marketplace/hooks/useMarketplace';
import type { ReviewFormProps, CreateReviewRequest } from '@/features/marketplace/types/marketplaceTypes';

const MAX_IMAGES = 5;
const MAX_PROS_CONS = 5;

export const ReviewForm: React.FC<ReviewFormProps> = ({
  productId,
  onSuccess,
  onCancel,
}) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pros, setPros] = useState<string[]>(['']);
  const [cons, setCons] = useState<string[]>(['']);
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { mutate: createReview, isPending } = useCreateReview({
    onSuccess: () => {
      onSuccess?.();
    },
  });
  
  const handleProChange = (index: number, value: string) => {
    const newPros = [...pros];
    newPros[index] = value;
    setPros(newPros);
  };
  
  const handleAddPro = () => {
    if (pros.length < MAX_PROS_CONS) {
      setPros([...pros, '']);
    }
  };
  
  const handleRemovePro = (index: number) => {
    setPros(pros.filter((_, i) => i !== index));
  };
  
  const handleConChange = (index: number, value: string) => {
    const newCons = [...cons];
    newCons[index] = value;
    setCons(newCons);
  };
  
  const handleAddCon = () => {
    if (cons.length < MAX_PROS_CONS) {
      setCons([...cons, '']);
    }
  };
  
  const handleRemoveCon = (index: number) => {
    setCons(cons.filter((_, i) => i !== index));
  };
  
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = MAX_IMAGES - images.length;
    const newFiles = files.slice(0, remainingSlots);
    
    // Create preview URLs
    const newPreviewUrls = newFiles.map((file) => URL.createObjectURL(file));
    
    setImages((prev) => [...prev, ...newFiles]);
    setPreviewUrls((prev) => [...prev, ...newPreviewUrls]);
  }, [images.length]);
  
  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  }, [previewUrls]);
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (rating === 0) {
      newErrors.rating = 'Please select a rating';
    }
    if (!title.trim()) {
      newErrors.title = 'Please enter a title';
    } else if (title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }
    if (!content.trim()) {
      newErrors.content = 'Please write your review';
    } else if (content.length < 20) {
      newErrors.content = 'Review must be at least 20 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    const filteredPros = pros.filter((p) => p.trim());
    const filteredCons = cons.filter((c) => c.trim());
    
    const data: CreateReviewRequest = {
      productId,
      rating,
      title: title.trim(),
      content: content.trim(),
      pros: filteredPros.length > 0 ? filteredPros : undefined,
      cons: filteredCons.length > 0 ? filteredCons : undefined,
      images: images.length > 0 ? images : undefined,
    };
    
    createReview(data);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Rating */}
      <div>
        <label className="block font-semibold text-navy mb-2">
          Overall Rating <span className="text-terracotta">*</span>
        </label>
        <StarRating
          rating={rating}
          size="lg"
          interactive
          onChange={setRating}
        />
        {errors.rating && (
          <p className="mt-1 text-sm text-terracotta">{errors.rating}</p>
        )}
      </div>
      
      {/* Title */}
      <div>
        <label htmlFor="review-title" className="block font-semibold text-navy mb-2">
          Review Title <span className="text-terracotta">*</span>
        </label>
        <input
          id="review-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summarize your experience"
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            ${errors.title ? 'border-terracotta' : ''}
          `}
          maxLength={200}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-terracotta">{errors.title}</p>
        )}
      </div>
      
      {/* Content */}
      <div>
        <label htmlFor="review-content" className="block font-semibold text-navy mb-2">
          Your Review <span className="text-terracotta">*</span>
        </label>
        <textarea
          id="review-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Share your experience with this product. What did you like? What could be improved?"
          rows={5}
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            resize-none
            ${errors.content ? 'border-terracotta' : ''}
          `}
        />
        <div className="flex justify-between mt-1">
          {errors.content && (
            <p className="text-sm text-terracotta">{errors.content}</p>
          )}
          <span className="text-xs text-warm-gray ml-auto">
            {content.length} characters
          </span>
        </div>
      </div>
      
      {/* Pros */}
      <div>
        <label className="block font-semibold text-navy mb-2">
          Pros <span className="text-warm-gray font-normal">(optional)</span>
        </label>
        <div className="space-y-2">
          {pros.map((pro, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-sage font-bold">+</span>
              <input
                type="text"
                value={pro}
                onChange={(e) => handleProChange(index, e.target.value)}
                placeholder="What did you like?"
                className="
                  flex-1 px-4 py-2.5 rounded-lg
                  bg-cream border-2 border-transparent
                  text-navy placeholder-warm-gray
                  focus:border-sage focus:outline-none
                  transition-colors duration-200
                "
              />
              {pros.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemovePro(index)}
                  className="p-2 text-warm-gray hover:text-terracotta transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          
          {pros.length < MAX_PROS_CONS && (
            <button
              type="button"
              onClick={handleAddPro}
              className="
                flex items-center gap-2
                text-sm text-sage-dark hover:text-sage
                transition-colors
              "
            >
              <Plus className="w-4 h-4" />
              Add another pro
            </button>
          )}
        </div>
      </div>
      
      {/* Cons */}
      <div>
        <label className="block font-semibold text-navy mb-2">
          Cons <span className="text-warm-gray font-normal">(optional)</span>
        </label>
        <div className="space-y-2">
          {cons.map((con, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-terracotta font-bold">−</span>
              <input
                type="text"
                value={con}
                onChange={(e) => handleConChange(index, e.target.value)}
                placeholder="What could be improved?"
                className="
                  flex-1 px-4 py-2.5 rounded-lg
                  bg-cream border-2 border-transparent
                  text-navy placeholder-warm-gray
                  focus:border-sage focus:outline-none
                  transition-colors duration-200
                "
              />
              {cons.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveCon(index)}
                  className="p-2 text-warm-gray hover:text-terracotta transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          
          {cons.length < MAX_PROS_CONS && (
            <button
              type="button"
              onClick={handleAddCon}
              className="
                flex items-center gap-2
                text-sm text-sage-dark hover:text-sage
                transition-colors
              "
            >
              <Plus className="w-4 h-4" />
              Add another con
            </button>
          )}
        </div>
      </div>
      
      {/* Images */}
      <div>
        <label className="block font-semibold text-navy mb-2">
          Photos <span className="text-warm-gray font-normal">(optional, max {MAX_IMAGES})</span>
        </label>
        
        <div className="flex flex-wrap gap-3">
          {previewUrls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-20 h-20 rounded-lg object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="
                  absolute -top-2 -right-2
                  w-6 h-6 rounded-full
                  bg-terracotta text-white
                  flex items-center justify-center
                  hover:bg-terracotta/80
                  transition-colors
                "
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          {images.length < MAX_IMAGES && (
            <label className="
              w-20 h-20 rounded-lg
              border-2 border-dashed border-warm-gray/30
              flex flex-col items-center justify-center
              cursor-pointer
              hover:border-sage hover:bg-sage/5
              transition-colors duration-200
            ">
              <Upload className="w-5 h-5 text-warm-gray mb-1" />
              <span className="text-xs text-warm-gray">Add</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-warm-gray/10">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="
              px-6 py-2.5 rounded-lg
              text-navy font-medium
              hover:bg-cream
              transition-colors duration-200
            "
          >
            Cancel
          </button>
        )}
        
        <button
          type="submit"
          disabled={isPending}
          className="
            px-6 py-2.5 rounded-lg
            bg-sage text-white font-semibold
            hover:bg-sage-dark
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors duration-200
            flex items-center gap-2
          "
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Review'
          )}
        </button>
      </div>
    </form>
  );
};

export default ReviewForm;

